import { Component, ChangeDetectionStrategy, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Plan, SubscriptionWithDetails, PlanInterval, SubscriptionStatus } from '../../models/subscription.model';

export type SortableSubColumns = 'user' | 'plan' | 'status' | 'end_date';

@Component({
  selector: 'app-plans-management',
  standalone: true,
  template: `
<div class="space-y-8">
  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
    <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
      <div>
          <h2 class="text-2xl font-bold text-slate-900">Gerenciamento de Planos</h2>
        <p class="text-sm text-gray-500">Crie e edite os planos de assinatura disponíveis.</p>
      </div>
      <button (click)="openCreateModal()" class="flex-shrink-0 flex items-center bg-custom-blue text-white font-semibold py-2 px-4 rounded-lg hover:bg-indigo-700 transition-opacity">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="w-5 h-5 mr-2"><path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" /></svg>
        Criar novo plano
      </button>
    </div>

    @if (plans().length > 0) {
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        @for (plan of plans(); track plan.id) {
          <div class="border border-gray-200  rounded-lg p-5 flex flex-col justify-between">
            <div>
              <h3 class="text-lg font-bold text-slate-900">{{ plan.name }}</h3>
              <p class="text-sm text-gray-500 dark:text-gray-400 mb-3">{{ plan.description }}</p>
              <p class="text-2xl font-bold text-custom-blue">R$ {{ (plan.price / 100).toFixed(2).replace('.', ',') }}</p>
              <p class="text-sm font-medium text-gray-600 dark:text-gray-400">{{ plan.credits_granted }} créditos / {{ plan.interval === 'credits' ? 'Pag. Único' : plan.interval }}</p>
            </div>
            <button (click)="openEditModal(plan)" class="mt-4 w-full bg-custom-gray text-custom-black font-semibold py-2 px-4 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm">Editar plano</button>
          </div>
        }
      </div>
    } @else {
      <p class="text-center text-gray-500 py-8">Nenhum plano cadastrado. Crie o primeiro!</p>
    }
  </div>

  <div class="bg-white p-6 md:p-8 rounded-2xl shadow-card border border-slate-200/80">
     <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h2 class="text-2xl font-bold text-slate-900">Assinaturas de Usuários</h2>
          <p class="text-sm text-gray-500">Visualize e filtre as assinaturas da plataforma.</p>
        </div>
        <div class="flex space-x-2 p-1 bg-custom-gray dark:bg-gray-700 rounded-lg">
          <button (click)="setSubscriptionFilter('all')" [class.bg-white]="subscriptionFilter() === 'all'" [class.dark:bg-gray-800]="subscriptionFilter() === 'all'" [class.text-custom-black]="subscriptionFilter() === 'all'" class="px-3 py-1 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300">Todas</button>
          <button (click)="setSubscriptionFilter('active')" [class.bg-white]="subscriptionFilter() === 'active'" [class.dark:bg-gray-800]="subscriptionFilter() === 'active'" [class.text-custom-black]="subscriptionFilter() === 'active'" class="px-3 py-1 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300">Ativas</button>
          <button (click)="setSubscriptionFilter('canceled')" [class.bg-white]="subscriptionFilter() === 'canceled'" [class.dark:bg-gray-800]="subscriptionFilter() === 'canceled'" [class.text-custom-black]="subscriptionFilter() === 'canceled'" class="px-3 py-1 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300">Canceladas</button>
          <button (click)="setSubscriptionFilter('expired')" [class.bg-white]="subscriptionFilter() === 'expired'" [class.dark:bg-gray-800]="subscriptionFilter() === 'expired'" [class.text-custom-black]="subscriptionFilter() === 'expired'" class="px-3 py-1 rounded-md text-sm font-medium text-gray-600 dark:text-gray-300">Expiradas</button>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-custom-gray dark:bg-gray-700">
            <tr>
              <th scope="col" (click)="sortBy('user')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Usuário</th>
              <th scope="col" (click)="sortBy('plan')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Plano</th>
              <th scope="col" (click)="sortBy('status')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Status</th>
              <th scope="col" (click)="sortBy('end_date')" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer">Data final</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200 dark:divide-gray-700">
            @for (sub of paginatedSubscriptions(); track sub.id) {
              <tr class="hover:bg-custom-gray dark:hover:bg-gray-700/50">
                <td class="px-6 py-4 whitespace-nowrap"><div class="text-sm font-medium">{{ sub.profiles?.name || '-' }}</div><div class="text-sm text-gray-500">{{ sub.profiles?.email || '-' }}</div></td>
                <td class="px-6 py-4 whitespace-nowrap text-sm">{{ sub.plans?.name || '-' }}</td>
                <td class="px-6 py-4 whitespace-nowrap"><span class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full" [class]="getSubscriptionStatusClass(sub.status)">{{ getSubscriptionStatusLabel(sub.status) }}</span></td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ sub.end_date ? (sub.end_date | date: 'dd/MM/yyyy') : 'N/A' }}</td>
              </tr>
            } @empty {
              <tr><td colspan="4" class="px-6 py-4 text-center text-gray-500">Nenhuma assinatura encontrada.</td></tr>
            }
          </tbody>
        </table>
      </div>
      <div class="flex items-center justify-between mt-4 text-sm">
        <span class="text-gray-600 dark:text-gray-400">Página {{ currentPage() }} de {{ totalPages() }}</span>
        <div class="flex items-center space-x-2">
          <button (click)="changePage(currentPage() - 1)" [disabled]="currentPage() === 1" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Anterior</button>
          <button (click)="changePage(currentPage() + 1)" [disabled]="currentPage() === totalPages()" class="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed">Próximo</button>
        </div>
      </div>
  </div>
</div>

@if (isModalOpen()) {
  <div class="fixed inset-0 bg-black bg-opacity-60 z-40 flex items-center justify-center" (click)="closeModal()">
    <div class="bg-white rounded-2xl shadow-card border border-slate-200/80 p-8 w-full max-w-lg" (click)="$event.stopPropagation()">
      <h3 class="text-xl font-bold mb-4">{{ editingPlan() ? 'Editar plano' : 'Criar novo plano' }}</h3>
      <form #planForm="ngForm" (ngSubmit)="savePlan()" class="space-y-4">
        <div><label for="planName" class="block text-sm font-medium">Nome do plano</label><input type="text" id="planName" name="planName" [(ngModel)]="planName" required class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm" placeholder="Ex: Concurseiro Pro"></div>
        <div><label for="planDescription" class="block text-sm font-medium">Descrição</label><input type="text" id="planDescription" name="planDescription" [(ngModel)]="planDescription" required class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm" placeholder="Para quem busca a excelência."></div>
        <div class="grid grid-cols-2 gap-4">
           <div><label for="planPrice" class="block text-sm font-medium">Preço (em centavos)</label><input type="number" id="planPrice" name="planPrice" [(ngModel)]="planPrice" required min="0" class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm" placeholder="5990"></div>
           <div><label for="planInterval" class="block text-sm font-medium">Intervalo</label><select id="planInterval" name="planInterval" [(ngModel)]="planInterval" class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm">@for(i of availableIntervals;track i.value){<option [value]="i.value">{{i.label}}</option>}</select></div>
        </div>
        <div><label for="planCredits" class="block text-sm font-medium">Créditos concedidos</label><input type="number" id="planCredits" name="planCredits" [(ngModel)]="planCredits" required min="0" class="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 px-3 py-2 sm:text-sm" placeholder="999"></div>
        <div class="flex justify-end space-x-3 pt-4 mt-4">
          <button type="button" (click)="closeModal()" class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-700">Cancelar</button>
          <button type="submit" [disabled]="!planForm.form.valid" class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-custom-blue hover:bg-indigo-700 disabled:bg-gray-400">Salvar</button>
        </div>
      </form>
    </div>
  </div>
}
`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule],
})
export class PlansManagementComponent {
  plans = input.required<Plan[]>();
  subscriptions = input.required<SubscriptionWithDetails[]>();
  planCreate = output<Omit<Plan, 'id' | 'created_at'>>();
  planUpdate = output<Plan>();

  readonly isModalOpen = signal(false);
  readonly editingPlan = signal<Plan | null>(null);
  readonly planName = signal('');
  readonly planDescription = signal('');
  readonly planPrice = signal(0);
  readonly planInterval = signal<PlanInterval>('month');
  readonly planCredits = signal(0);
  readonly availableIntervals: { value: PlanInterval, label: string }[] = [
    { value: 'month', label: 'Mensal' },
    { value: 'quarter', label: 'Trimestral' },
    { value: 'year', label: 'Anual' },
    { value: 'credits', label: 'Pagamento Único (Créditos)' }
  ];

  readonly subscriptionFilter = signal<SubscriptionStatus | 'all'>('all');
  readonly sortColumn = signal<SortableSubColumns>('end_date');
  readonly sortDirection = signal<'asc' | 'desc'>('desc');
  readonly currentPage = signal(1);
  readonly pageSize = signal(10);

  readonly processedSubscriptions = computed(() => {
    const filter = this.subscriptionFilter();
    const column = this.sortColumn();
    const direction = this.sortDirection();

    const subs = this.subscriptions().filter(s => filter === 'all' || s.status === filter);

    subs.sort((a, b) => {
      let aValue: string | number = '';
      let bValue: string | number = '';

      switch (column) {
        case 'user':
          aValue = a.profiles?.name || '';
          bValue = b.profiles?.name || '';
          break;
        case 'plan':
          aValue = a.plans?.name || '';
          bValue = b.plans?.name || '';
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'end_date':
          aValue = a.end_date ? new Date(a.end_date).getTime() : 0;
          bValue = b.end_date ? new Date(b.end_date).getTime() : 0;
          break;
      }

      const comparison = String(aValue).localeCompare(String(bValue), undefined, { numeric: column === 'end_date' });
      return direction === 'asc' ? comparison : -comparison;
    });

    return subs;
  });

  readonly totalPages = computed(() => Math.ceil(this.processedSubscriptions().length / this.pageSize()));

  readonly paginatedSubscriptions = computed(() => {
    const startIndex = (this.currentPage() - 1) * this.pageSize();
    return this.processedSubscriptions().slice(startIndex, startIndex + this.pageSize());
  });

  openCreateModal() {
    this.editingPlan.set(null);
    this.planName.set('');
    this.planDescription.set('');
    this.planPrice.set(0);
    this.planInterval.set('month');
    this.planCredits.set(0);
    this.isModalOpen.set(true);
  }

  openEditModal(plan: Plan) {
    this.editingPlan.set(plan);
    this.planName.set(plan.name);
    this.planDescription.set(plan.description);
    this.planPrice.set(plan.price);
    this.planInterval.set(plan.interval);
    this.planCredits.set(plan.credits_granted);
    this.isModalOpen.set(true);
  }

  closeModal() {
    this.isModalOpen.set(false);
  }

  savePlan() {
    const planData = {
      name: this.planName(),
      description: this.planDescription(),
      price: this.planPrice(),
      interval: this.planInterval(),
      credits_granted: this.planCredits(),
    };

    if (this.editingPlan()) {
      this.planUpdate.emit({ ...this.editingPlan()!, ...planData });
    } else {
      this.planCreate.emit(planData);
    }
    this.closeModal();
  }

  setSubscriptionFilter(filter: SubscriptionStatus | 'all') {
    this.subscriptionFilter.set(filter);
    this.currentPage.set(1);
  }

  sortBy(column: SortableSubColumns) {
    if (this.sortColumn() === column) {
      this.sortDirection.update(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
    this.currentPage.set(1);
  }

  changePage(page: number) {
    if (page > 0 && page <= this.totalPages()) {
      this.currentPage.set(page);
    }
  }

  getSubscriptionStatusClass(status: SubscriptionStatus): string {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300';
      case 'canceled': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300';
      case 'expired': return 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300';
    }
  }

  getSubscriptionStatusLabel(status: SubscriptionStatus): string {
    switch (status) {
      case 'active': return 'Ativa';
      case 'canceled': return 'Cancelada';
      case 'expired': return 'Expirada';
    }
  }
}
